import { type ComponentType, type JSX, lazy } from "react";
import { Navigate } from "react-router-dom";
import { useAppSelector } from "../lib/store/dynamicStore";

// Lazy import for ProtectedRoute wrapper
const ProtectedRoute = lazy(
	() => import("../einstein/components/auth/ProtectedRoute"),
);

// Helper function for lazy loading named exports from barrel files without using any
export const lazyFromBarrel = <TModule extends Record<string, unknown>>(
	importFn: () => Promise<TModule>,
	exportName: keyof TModule,
) =>
	lazy<ComponentType>(async () => {
		const module = await importFn();
		const exported = module[exportName];
		return { default: exported as ComponentType };
	});

// Helper to wrap components with ProtectedRoute
export const protectedRoute = (component: JSX.Element): JSX.Element => (
	<ProtectedRoute>{component}</ProtectedRoute>
);

// Auth guard for login page - redirects to home if already authenticated
export const AuthGuard = ({ children }: { children: JSX.Element }) => {
	const isAuthenticated = useAppSelector(
		(state) => state.auth.isAuthenticated ?? false,
	);
	const isLoading = useAppSelector((state) => state.auth.isLoading);

	// Don't redirect while auth is still loading (handles MSAL redirect callbacks)
	if (isLoading) {
		return children;
	}

	if (isAuthenticated) {
		return <Navigate to="/" replace />;
	}

	return children;
};
